import { useState, useEffect, useCallback } from "react";
import {
  Paper,
  Grid,
  TableContainer,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  useTheme,
  TextField,
  Button,
  Stack,
  Dialog,
  Autocomplete,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import {
  TrashIcon,
  PlusIcon,
  PenIcon,
  ConfigIcon,
} from "../../../components/svgicons/SvgIcons";
import FancyTooltip from "../../../components/utils/FancyTooltip";
import FancyPaper from "../../../components/FancyPaper";
import { useForm } from "react-hook-form";
import {
  getRequestBooking,
  createRequest,
  uploadDocuments,
  updateRequest,
  updateInfoConfRequest,
  findDocuments,
  getUrlImage,
  deleteDocuments,
  deleteRequest,
  uploadExcelCategories,
} from "../../../services/booking/BookingService";
import { properties } from "../../../utils/Properties_es";
import { useApp } from "../../../hooks/useApp";
import AddOrUpdateBookingModal from "../../../components/utils/AddOrUpdateBookingModal";
import DeleteModal from "../../../components/utils/DeleteModal";
import UpdateBookingModal from "../../../components/utils/UpdateBookingModal";
import FancyTablePagination from "../../../components/utils/FancyTablePagination";
import ResourceAccess from "../../../components/security/ResourceAccess";
import { useTranslation } from "react-i18next";
import { setLastCategory } from "../../../utils/LocalStorageManager";
import Swal from "sweetalert2";

const SettingBooking = () => {
  const { t } = useTranslation();
  const {
    authInfo,
    company,
    isAdmin,
    setLoading,
    setErrorMsg,
    modalData,
    setModalData,
    setSuccessMsg,
    errorMsg,
    setCategoryFilter,
    categoryFilter,
  } = useApp();

  const theme = useTheme();
  const [installationData, setInstallationData] = useState<any>([]);
  const [filerInstallationData, setFilerInstallationData] = useState<any>([]);
  const [preFilter, setPreFilter] = useState<any>("");
  const [page, setPage] = useState(0);
  const [filteredPhotos, setFilteredPhotos] = useState<any>([]);
  const [logosByArea, setLogosByArea] = useState<any>({});
  const [images, setImages] = useState<any>({});
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const initForm = {
    id: 0,
    description: "",
    status: "",
  };

  const categoryOptions = [
    { id: "installation", name: t("installation") },
    { id: "transportation", name: t("transportation") },
  ];

  const [formData, setFormData] = useState<any>(initForm);

  const { handleSubmit } = useForm();

  // Utilidad para obtener el id de la compañía
  const getCompanyId = useCallback(() => (isAdmin ? company : authInfo.companyId), [isAdmin, company, authInfo.companyId]);

  // Carga inicial de registros
  useEffect(() => {
    handleFetchData(0, "");
    // eslint-disable-next-line
  }, [company]);

  // Filtrado por categoría
  const filterForCategory = useCallback((filter: string) => {
    setLastCategory(filter);

    if (!installationData || !installationData.content) return;

    if (!filter) {
      setFilerInstallationData(installationData);
      return;
    }

    const filteredContent = installationData.content.filter((item: any) => item.type === filter);

    setFilerInstallationData({
      ...installationData,
      content: filteredContent,
      numberOfElements: filteredContent.length,
    });
  }, [installationData]);

  useEffect(() => {
    if (installationData?.content?.length > 0) {
      filterForCategory(categoryFilter);
    }
  }, [installationData, categoryFilter, company, filterForCategory]);

  // Obtener fotos de un área
  const getPhotos = useCallback(async (idArea: string, areaDetailId?: string) => {
    return await findDocuments(getCompanyId(), idArea, areaDetailId);
  }, [getCompanyId]);

  // Cargar logos por área
  useEffect(() => {
    const fetchLogos = async () => {
      const logos: any = {};
      const images: any = {};

      for (const area of filerInstallationData.content ?? []) {
        const areaId = area.id;
        const areaImages: any = await getPhotos(areaId);

        const filteredImages = Array.isArray(areaImages)
          ? areaImages.filter((img: any) => img.idDocumentCategory.id === 2)
          : [];

        images[areaId] = filteredImages;

        const imageUrlResults = await Promise.all(
          filteredImages.map(async (img: any) => {
            try {
              return await getUrlImage(img.id);
            } catch {
              return null;
            }
          })
        );

        logos[areaId] = imageUrlResults.filter((url: any) => url !== null);
      }

      setLogosByArea(logos);
      setImages(images);
    };

    fetchLogos();
  }, [filerInstallationData, getPhotos]);

  // Buscar registros
  const handleFetchData = useCallback(async (currentPage: number, filter: string, id?: string) => {
    setLoading && setLoading(true);
    setInstallationData([]);
    try {
      setPreFilter(filter);
      setPage(currentPage);

      let bookingData = await getRequestBooking(
        currentPage,
        filter,
        getCompanyId()
      );
      if (bookingData) {
        setInstallationData(bookingData);
        setFilerInstallationData(bookingData);
      }

      if (id) {
        const images: any = await getPhotos(id);
        const filteredImages = images?.filter(
          (img: any) => img.idDocumentCategory.id === 1
        );
        setFilteredPhotos(filteredImages);
      }

      setLoading && setLoading(false);
    } catch (error: any) {
      setLoading && setLoading(false);
      setErrorMsg && setErrorMsg(error.message);
    }
  }, [getCompanyId, getPhotos, setErrorMsg, setLoading]);

  // Crear registro
  const handleAdd = async (data: any) => {
    handleCancelModal();
    setLoading && setLoading(true);
    try {
      let createData = await createRequest(
        {
          ...data,
          userCreated: authInfo.username,
          status: data.status.id,
        },
        getCompanyId()
      );

      const idArea = createData.id;

      if (!createData) {
        setErrorMsg && setErrorMsg(t("errorCreatingCategory"));
        setLoading && setLoading(false);
        return;
      }
      // Subir documentos
      for (const [category, files] of [
        ["1", data.documents],
        ["2", data.logo],
      ] as [string, any[]][]) {
        if (files?.length) {
          const formData = new FormData();
          formData.append("idDocumentCategory", category);
          formData.append("idCompany", getCompanyId());
          formData.append("idArea", idArea);
          for (let i = 0; i < files.length; i++) {
            formData.append("files", files[i]);
          }
          formData.append("user", authInfo.username);
          await uploadDocuments(formData);
        }
      }

      if (createData.code === "001") {
        setErrorMsg && setErrorMsg(createData.message);
        setLoading && setLoading(false);
        return;
      }
      setLoading && setLoading(false);
      setSuccessMsg && setSuccessMsg(t("successfullyCreatedCategory"));
      await handleFetchData(0, "");
    } catch (error: any) {
      setErrorMsg && setErrorMsg(error.message);
      setLoading && setLoading(false);
    }
  };

  // Actualizar registro
  const handleUpdate = async (data: any) => {
    handleCancelModal();
    setLoading && setLoading(true);
    try {
      let updateData = await updateRequest(
        {
          ...data,
          modifierUser: authInfo?.username,
          status: data.status.id,
        },
        getCompanyId()
      );

      if (!updateData) {
        setErrorMsg && setErrorMsg(t("errorUpdatingCategory"));
        setLoading && setLoading(false);
        return;
      }

      // Documentos
      if (data.documents.length > filteredPhotos.length) {
        for (let i = 0; i < data.documents.length; i++) {
          const element = data.documents[i];
          if (!!element) {
            const formData = new FormData();
            formData.append("idDocumentCategory", "1");
            formData.append("idCompany", getCompanyId());
            formData.append("idArea", data.id);
            formData.append("files", element);
            formData.append("user", authInfo.username);
            await uploadDocuments(formData);
          }
        }
      } else {
        for (let i = 0; i < data.documents.length; i++) {
          const element = data.documents[i];
          if (!!element) {
            await deleteDocuments(filteredPhotos[i].id, authInfo.username);
            const formData = new FormData();
            formData.append("idDocumentCategory", "1");
            formData.append("idCompany", getCompanyId());
            formData.append("idArea", data.id);
            formData.append("files", element);
            formData.append("user", authInfo.username);
            await uploadDocuments(formData);
          }
        }
      }

      // Logo
      const logoUpdate = data.logo[0];
      if (!!logoUpdate) {
        if (images[data.id]?.length > 0) {
          for (const logo of images[data.id]) {
            await deleteDocuments(logo.id, authInfo.username);
          }
        }
        const formData = new FormData();
        formData.append("idDocumentCategory", "2");
        formData.append("idCompany", getCompanyId());
        formData.append("idArea", data.id);
        formData.append("files", logoUpdate);
        formData.append("user", authInfo.username);
        await uploadDocuments(formData);
      }

      setLoading && setLoading(false);
      setSuccessMsg && setSuccessMsg(t("categoryUpdatedCorrectly"));
      await handleFetchData(0, "");
    } catch (error: any) {
      setErrorMsg && setErrorMsg(error.message);
      setLoading && setLoading(false);
    }
  };

  // Actualizar configuraciones
  const handleInstallationUpdate = async (data: any, id: any) => {
    handleCancelModal();
    setLoading && setLoading(true);
    try {
      let hasError = false;
      let nameError = "";

      for (let i = 0; i < data.installations.length; i++) {
        let updateData = await updateInfoConfRequest(
          {
            ...data.installations[i],
            userModified: authInfo?.username,
            idArea: id,
          },
          getCompanyId()
        );

        if (updateData.code === "001") {
          hasError = true;
          nameError += `${data.installations[i].description}; `;
        }
      }

      if (hasError) {
        setErrorMsg && setErrorMsg(`${nameError} No se pudo actualizar`);
        setLoading && setLoading(false);
        return;
      }

      setLoading && setLoading(false);
      setSuccessMsg &&
        setSuccessMsg(properties.com_parval_label_booking_update_conf_sucess);

      await handleFetchData(0, "");
    } catch (error: any) {
      setErrorMsg && setErrorMsg(error.message);
      setLoading && setLoading(false);
    }
  };

  // Eliminar registro
  const handleDelete = async (id: any) => {
    handleCancelModal();
    setLoading && setLoading(true);
    try {
      let deleteData = await deleteRequest(getCompanyId(), id);
      if (!deleteData) {
        setErrorMsg && setErrorMsg(t("errorDeletingCategory"));
        setLoading && setLoading(false);
        return;
      }
      setLoading && setLoading(false);
      setSuccessMsg && setSuccessMsg(t("categorySuccessfullyRemoved"));
      setPage(0);
      await handleFetchData(0, preFilter);
    } catch (error: any) {
      setErrorMsg && setErrorMsg(error.message);
      setLoading && setLoading(false);
    }
  };

  // Validar errores al abrir modal
  useEffect(() => {
    if (modalData?.modalOpen && errorMsg) {
      setModalData &&
        setModalData({
          modalOpen: false,
          modalType: "",
          modalObject: null,
        });
    }
  }, [modalData, errorMsg, setModalData]);

  // Abrir modal
  const handleOpenModal = async (event: any) => {
    event.preventDefault();
    const modalAction = event.currentTarget.getAttribute("data-name");
    const id = event.currentTarget.getAttribute("data-id");
    let object = null;

    if (modalAction === "update" || modalAction === "updateInstallation") {
      object = filerInstallationData.content.find(
        (p: any) => p.id === parseInt(id)
      );
      setFormData(object);

      const images: any = await getPhotos(id);
      const filteredImages = images?.filter(
        (img: any) => img.idDocumentCategory.id === 1
      );
      setFilteredPhotos(filteredImages);
    }

    if (modalAction === "delete") {
      object = filerInstallationData.content.find(
        (p: any) => p.id === parseInt(id)
      );
    }

    setModalData &&
      setModalData({
        ...modalData,
        modalOpen: true,
        modalType: modalAction,
        modalObject: object,
      });
  };

  // Cerrar modal
  const handleCancelModal = () => {
    if (modalData?.modalType !== "delete") {
      setFormData(initForm);
    }
    setFilteredPhotos([]);
    setModalData &&
      setModalData({
        ...modalData,
        modalOpen: false,
        modalType: "",
        modalObject: null,
      });
  };

  // Cambiar página
  const handleChangePage = async (_: unknown, newPage: number) => {
    const customPage = newPage - 1;
    setPage(customPage);
    if (customPage !== page) {
      await handleFetchData(customPage, preFilter);
    }
  };

  // Cambiar filas por página
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Enviar formulario
  const onSubmit = async (data: any) => {
    switch (modalData?.modalType) {
      case "create":
        await handleAdd(data);
        break;
      case "update":
        await handleUpdate(data);
        break;
      case "updateInstallation":
        //@ts-ignore
        await handleInstallationUpdate(data, modalData?.modalObject?.id);
        break;
      case "delete":
        //@ts-ignore
        await handleDelete(modalData?.modalObject?.id);
        break;
      default:
        break;
    }
    if (modalData?.modalType !== "delete") {
      setFormData(formData);
    }
  };

  // Carga masiva desde Excel
  const handleBulkUpload = async () => {
    try {
      const input = document.createElement("input");
      input.type = "file";
      input.accept = ".xlsx,.xls";
      input.onchange = async (event: any) => {
        const file = event.target.files?.[0];
        if (file) {
          const result = await Swal.fire({
            title: t("confirmBulkUpload"),
            text: `${t("bulkUploadConfirmText")}\n\n${t("selectedFile")}: ${file.name}`,
            icon: "question",
            showCancelButton: true,
            confirmButtonColor: "#0A3A7A",
            cancelButtonColor: "#d33",
            confirmButtonText: t("yes"),
            cancelButtonText: t("cancel"),
          });

          if (result.isConfirmed) {
            setLoading && setLoading(true);
            try {
              const formData = new FormData();
              formData.append("file", file);
              formData.append("companyId", getCompanyId());

              await uploadExcelCategories(formData);

              setSuccessMsg && setSuccessMsg(t("bulkUploadSuccess"));
              await handleFetchData(0, "");
            } catch (error: any) {
              setErrorMsg && setErrorMsg(error.message);
            } finally {
              setLoading && setLoading(false);
            }
          }
        }
      };
      input.click();
    } catch (error: any) {
      setErrorMsg && setErrorMsg(error.message);
    }
  };

  return (
    <>
      <FancyPaper pagetitle={`${t("maintenance")} / ${t("categories")}`}>
        <Grid container spacing={8} sx={{ pb: 8 }}>
          <Grid item md={12} sm={6} xs={12}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
              <Stack direction="row" alignContent="center" alignItems="center" spacing={2}>
                <ResourceAccess isCode={true} pathOrCode={"USER:WRITE"}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleOpenModal}
                    data-name="create"
                  >
                    {t("addCategory")}
                    <PlusIcon sx={{ ml: 1 }} />
                  </Button>
                </ResourceAccess>
                {categoryFilter === "transportation" && (
                  <ResourceAccess isCode={true} pathOrCode={"CATEGORY:WRITE"}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleBulkUpload}
                      startIcon={<CloudUploadIcon />}
                    >
                      {t("bulkUpload")}
                    </Button>
                  </ResourceAccess>
                )}
              </Stack>
              <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={2}>
                <Autocomplete
                  value={
                    categoryFilter
                      ? categoryOptions.find((option) => option.id === categoryFilter)
                      : null
                  }
                  onChange={async (_, newValue) => {
                    const newCategoryFilter = newValue?.id || "";
                    setCategoryFilter(newCategoryFilter);
                    if (newCategoryFilter !== categoryFilter) {
                      setPage(0);
                      await handleFetchData(0, preFilter);
                    }
                  }}
                  options={categoryOptions}
                  getOptionLabel={(option) => option?.name || ""}
                  isOptionEqualToValue={(option, value) => option?.id === value?.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("category")}
                      variant="standard"
                      fullWidth
                      sx={{
                        marginTop: -5,
                        width: 200,
                        "& fieldset": { borderColor: "blue" },
                        "& svg": { color: "blue" },
                      }}
                    />
                  )}
                />
              </Stack>
            </Stack>
          </Grid>
        </Grid>
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }} aria-label="simple table">
            <TableHead>
              <TableRow>
                <TableCell align="center">{t("name")}</TableCell>
                <TableCell align="center">{t("description")}</TableCell>
                <TableCell align="center">{t("state")}</TableCell>
                <TableCell align="center">{t("logo")}</TableCell>
                <TableCell align="center">{t("actions")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filerInstallationData?.content?.map((row: any, i: number) => (
                <TableRow key={i} sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                  <TableCell align="center">{row.name}</TableCell>
                  <TableCell align="center">{row.description}</TableCell>
                  <TableCell align="center">
                    {row.status === 0 ? t("active") : t("idle")}
                  </TableCell>
                  <TableCell align="center" style={{ padding: "8px", maxWidth: "100px" }}>
                    <img src={logosByArea[row.id]?.[0]} style={{ width: "27%", height: "auto" }} />
                  </TableCell>
                  <TableCell align="center">
                    <ResourceAccess isCode={true} pathOrCode={"USER:WRITE"}>
                      <FancyTooltip title={t("edit")} placement="top">
                        <IconButton
                          aria-label="edit"
                          component="label"
                          color="secondary"
                          sx={{ "&:hover": { color: theme.palette.secondary.dark } }}
                          onClick={handleOpenModal}
                          data-name="update"
                          data-id={row.id}
                        >
                          <PenIcon />
                        </IconButton>
                      </FancyTooltip>
                      <FancyTooltip title={t("confFacilities")} placement="top">
                        <IconButton
                          aria-label="sorter"
                          component="label"
                          color="secondary"
                          sx={{ "&:hover": { color: theme.palette.secondary.dark } }}
                          data-id={row.id}
                          onClick={handleOpenModal}
                          data-name="updateInstallation"
                        >
                          <ConfigIcon />
                        </IconButton>
                      </FancyTooltip>
                      <FancyTooltip title={t("delete")} placement="top">
                        <IconButton
                          aria-label="trash"
                          component="label"
                          color="secondary"
                          sx={{ "&:hover": { color: theme.palette.secondary.dark } }}
                          data-id={row.id}
                          onClick={handleOpenModal}
                          data-name="delete"
                        >
                          <TrashIcon />
                        </IconButton>
                      </FancyTooltip>
                    </ResourceAccess>
                  </TableCell>
                </TableRow>
              ))}
              {(!filerInstallationData?.content || filerInstallationData.content.length <= 0) && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    {t("thereAreNoRecords")}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <FancyTablePagination
          count={filerInstallationData?.content?.length > 0 ? filerInstallationData?.content?.length : 0}
          rowsPerPage={installationData?.size || 10}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          totalElements={installationData?.totalElements || 0}
          totalPages={installationData?.totalPages || 0}
        />
      </FancyPaper>
      {(modalData?.modalType === "create" || modalData?.modalType === "update") && (
        <Dialog open={modalData.modalOpen} onClose={handleCancelModal} fullWidth>
          <AddOrUpdateBookingModal
            data={formData}
            filteredPhotos={filteredPhotos}
            refreshData={handleFetchData}
            setFilteredPhotos={setFilteredPhotos}
            logosByArea={images}
            onSubmit={onSubmit}
            cancelModal={handleCancelModal}
            modalType={modalData?.modalType}
          />
        </Dialog>
      )}
      {modalData?.modalType === "updateInstallation" && (
        <Dialog open={modalData.modalOpen} onClose={handleCancelModal} fullWidth>
          <UpdateBookingModal
            data={formData}
            filteredPhotos={filteredPhotos}
            refreshData={handleFetchData}
            cancelModal={handleCancelModal}
            modalType={modalData?.modalType}
            areaHasCapacity={formData?.capacity !== null && formData?.capacity !== 0}
            bookingTime={formData?.bookingTime}
          />
        </Dialog>
      )}
      {modalData?.modalType === "delete" && (
        <Dialog open={modalData.modalOpen} onClose={handleCancelModal} fullWidth>
          <DeleteModal
            //@ts-ignore
            textChildren={modalData?.modalObject?.username}
            actionButton={
              <>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  sx={{ mt: 2, mr: 2 }}
                  onClick={handleSubmit(onSubmit)}
                >
                  {t("delete")}
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  sx={{ mt: 2, mr: 2 }}
                  onClick={handleCancelModal}
                  autoFocus
                >
                  {t("cancel")}
                </Button>
              </>
            }
          />
        </Dialog>
      )}
    </>
  );
};

export default SettingBooking;